import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, RefreshCw, Maximize2, Bot, ShoppingCart, Camera, ImageIcon } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import ChatMarkdown from "./ChatMarkdown";

const SESSION_KEY = "cap_chat_session_id";

const getOrCreateSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const TypingDots = () => (
  <div className="flex items-center gap-1 px-3 py-2">
    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
  </div>
);

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();
  return (
    <div className="flex items-center gap-2 bg-[#0A0A0A] border border-white/10 rounded-sm p-2 hover:border-[#D62828]/50 transition-colors" data-testid={`chat-product-${product.product_id}`}>
      <div className="w-10 h-10 bg-[#1A1A1A] rounded-sm overflow-hidden flex-shrink-0">
        <img src={product.image_url || 'https://images.unsplash.com/photo-1692486407015-3a47e4c1181c'} alt={product.name} className="w-full h-full object-cover" />
      </div>
      <div className="flex-1 min-w-0">
        <Link to={`/products/${product.product_id}`} className="block hover:text-[#D4AF37] transition-colors">
          <p className="text-white text-xs font-semibold line-clamp-1">{product.name}</p>
        </Link>
        <p className="text-[#D62828] text-xs font-bold">${product.online_price?.toFixed(2)}</p>
      </div>
      <Link
        to={`/products/${product.product_id}`}
        className="flex-shrink-0 h-7 px-2 border border-white/20 hover:border-[#D4AF37] text-white text-[10px] uppercase tracking-wide font-bold rounded-sm flex items-center transition-colors"
        title="View product"
        data-testid={`chat-view-${product.product_id}`}
      >View</Link>
      <button
        onClick={() => addToCart(product.product_id)}
        className="flex-shrink-0 w-7 h-7 bg-[#D62828] hover:bg-[#B91C1C] rounded-sm flex items-center justify-center transition-colors"
        title="Add to cart"
        data-testid={`chat-add-to-cart-${product.product_id}`}
      >
        <ShoppingCart className="w-3.5 h-3.5 text-white" />
      </button>
    </div>
  );
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 100);
      if (messages.length === 0) {
        axios.get(`${API}/chat/history/${sessionId}`)
          .then(res => {
            if (res.data.messages?.length > 0) {
              setMessages(res.data.messages);
            } else {
              setMessages([{ role: "assistant", content: "Hey there! I'm your Cut Above Polish Shop assistant. Ask me about products, polishing tips, VIP membership, or shipping. What can I help you with?", suggested_products: [] }]);
            }
          })
          .catch(() => {
            setMessages([{ role: "assistant", content: "Hey there! I'm your Cut Above Polish Shop assistant. What can I help you with?", suggested_products: [] }]);
          });
      }
    }
  }, [open, sessionId]);

  useEffect(scrollToBottom, [messages]);

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/message`, { session_id: sessionId, message: text, user_id: user?.user_id || null });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response, suggested_products: res.data.suggested_products || [] }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble right now. Call us at 352-808-8981.", suggested_products: [] }]);
    }
    setLoading(false);
  };

  const clearChat = async () => {
    await axios.delete(`${API}/chat/history/${sessionId}`).catch(() => {});
    sessionStorage.removeItem(SESSION_KEY);
    window.location.reload();
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = "";

    // Preview as user message
    const previewUrl = URL.createObjectURL(file);
    setMessages(prev => [...prev, {
      role: "user",
      content: null,
      image_preview: previewUrl,
      image_name: file.name
    }]);
    setUploadingPhoto(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("session_id", sessionId);
      const res = await axios.post(`${API}/chat/upload-photo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMessages(prev => [...prev, {
        role: "assistant",
        content: res.data.analysis,
        suggested_products: res.data.suggested_products || []
      }]);
    } catch {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Sorry, I couldn't analyze that photo. Please try a JPEG or PNG under 10MB.",
        suggested_products: []
      }]);
    }
    setUploadingPhoto(false);
  };

  const quickReplies = ["Best compound for scratches?", "Tell me about VIP", "Shipping info", "How to polish aluminum?"];

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#D62828] hover:bg-[#B91C1C] text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 btn-glow" data-testid="chat-bubble-btn" aria-label="Open chat">
          <MessageCircle className="w-7 h-7" />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D4AF37] rounded-full animate-pulse"></span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] sm:w-[400px] max-h-[600px] flex flex-col bg-[#0F0F0F] border border-white/10 rounded-sm shadow-2xl" data-testid="chat-window">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-[#1A1A1A] border-b border-white/10 rounded-t-sm">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 bg-[#D62828] rounded-full flex items-center justify-center"><Bot className="w-5 h-5 text-white" /></div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#1A1A1A]"></span>
              </div>
              <div>
                <p className="font-semibold text-white text-sm">Cut Above Assistant</p>
                <p className="text-green-400 text-xs">Online · Claude AI</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/chat" onClick={() => setOpen(false)} className="p-1.5 text-gray-400 hover:text-white transition-colors" title="Full chat" data-testid="chat-expand-btn"><Maximize2 className="w-4 h-4" /></Link>
              <button onClick={clearChat} className="p-1.5 text-gray-400 hover:text-white transition-colors" title="New chat" data-testid="chat-clear-btn"><RefreshCw className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} className="p-1.5 text-gray-400 hover:text-white transition-colors" data-testid="chat-close-btn"><X className="w-4 h-4" /></button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0 max-h-[400px]">
            {messages.map((msg, idx) => (
              <div key={idx}>
                <div className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  {msg.role === "assistant" && (
                    <div className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center flex-shrink-0 mr-2 mt-1">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-sm text-sm leading-relaxed ${msg.role === "user" ? "bg-[#D62828] text-white rounded-br-none" : "bg-[#1A1A1A] text-gray-200 border border-white/5 rounded-bl-none"}`} data-testid={`chat-msg-${idx}`}>
                    {msg.image_preview ? (
                      <div className="p-1">
                        <img src={msg.image_preview} alt={msg.image_name || "upload"} className="max-w-[180px] rounded-sm object-cover" />
                        <p className="text-xs text-white/70 px-1 pt-1">Photo sent for analysis</p>
                      </div>
                    ) : (
                      <ChatMarkdown text={msg.content} className="block px-3 py-2 whitespace-pre-wrap" />
                    )}
                  </div>
                </div>
                {/* Product cards under AI messages */}
                {msg.role === "assistant" && msg.suggested_products?.length > 0 && (
                  <div className="ml-9 mt-2 space-y-1.5" data-testid={`chat-products-${idx}`}>
                    {msg.suggested_products.map(p => <ProductCard key={p.product_id} product={p} />)}
                  </div>
                )}
              </div>
            ))}
            {(loading || uploadingPhoto) && (
              <div className="flex justify-start">
                <div className="w-7 h-7 bg-[#D62828] rounded-full flex items-center justify-center flex-shrink-0 mr-2"><Bot className="w-4 h-4 text-white" /></div>
                <div className="bg-[#1A1A1A] border border-white/5 rounded-sm rounded-bl-none">
                  {uploadingPhoto ? (
                    <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400">
                      <ImageIcon className="w-3.5 h-3.5 animate-pulse text-[#D62828]" />
                      Analyzing photo...
                    </div>
                  ) : (
                    <TypingDots />
                  )}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies for new chat */}
          {messages.length <= 1 && !loading && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {quickReplies.map((q, i) => (
                <button key={i} onClick={() => { setInput(q); inputRef.current?.focus(); }} className="text-xs px-2 py-1 bg-[#1A1A1A] border border-white/10 rounded-sm text-gray-300 hover:border-[#D62828] hover:text-white transition-colors" data-testid={`quick-reply-${i}`}>{q}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <form onSubmit={sendMessage} className="flex items-center gap-2 px-4 py-3 border-t border-white/10 bg-[#151515] rounded-b-sm">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoUpload}
              className="hidden"
              data-testid="chat-photo-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploadingPhoto}
              title="Upload photo for AI analysis"
              className="w-9 h-9 bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-white/10 hover:border-[#D62828]/50 rounded-sm flex items-center justify-center transition-colors disabled:opacity-40 flex-shrink-0"
              data-testid="chat-photo-upload-btn"
            >
              <Camera className="w-4 h-4 text-gray-400" />
            </button>
            <input ref={inputRef} type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about products, polishing tips..." className="flex-1 bg-[#0F0F0F] border border-white/10 focus:border-[#D62828] text-white placeholder:text-gray-600 rounded-sm px-3 py-2 text-sm outline-none" data-testid="chat-input" disabled={loading || uploadingPhoto} />
            <button type="submit" disabled={loading || uploadingPhoto || !input.trim()} className="w-9 h-9 bg-[#D62828] hover:bg-[#B91C1C] rounded-sm flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0" data-testid="chat-send-btn">
              <Send className="w-4 h-4 text-white" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
